import React from "react";
import { Typography, Tooltip } from "@mui/material";
import Icon from "@mdi/react";
import { mdiArrowLeft, mdiArrowRight } from "@mdi/js";
import { useTranslation } from "react-i18next";
import { translateReactNode } from "../../../../utils/translateReactNode";
import './SlideShow.css';

export interface Slide {
  imageSrc: string;
  caption: string;
  text: React.ReactNode;
}

interface SlideShowProps {
  slides: Slide[];
  index: number;
  onChange: (index: number) => void;
  hideText?: boolean;
  cohort: string;
}

const SlideShow: React.FC<SlideShowProps> = ({ slides, index, onChange , hideText, cohort }) => {
  const { t } = useTranslation(cohort);
  return (
    <div style={{ position: 'relative', textAlign: 'center', width: '100%' }}>
      <div className="slideshow-content">
        {!hideText &&
          <div style={{ flex: 2, display: 'flex', justifyContent: 'center', pointerEvents: 'auto', margin: '1rem' }}>
            <Typography variant="subtitle1" component="div" sx={{ background: 'rgba(255,255,255,0.7)', px: 2, borderRadius: 2 }}>
              {translateReactNode(slides[index].text, cohort)}
            </Typography>
          </div>
        }
        <img
          src={slides[index].imageSrc}
          alt={slides[index].caption}
          aria-label={slides[index].caption}
          className="slideshow-image"
        />
      </div>
      {/* Controls overlay */}
      <div className="slideshow-controls-overlay">
        <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-start', pointerEvents: 'auto' }}>
          <Tooltip title={index === 0 ? t("No previous slide available") : t("Previous slide")} arrow>
            <span>
              <button
                className={"button-circular " + (index === 0 ? "disabled" : "")}
                onClick={() => onChange(index - 1)}
                disabled={index === 0}
                style={{
                  width: 40,
                  height: 40,
                  fontSize: 20
                }}
                aria-label={t("Previous slide")}
              >
                <Icon path={mdiArrowLeft} size={1.2} />
              </button>
            </span>
          </Tooltip>
        </div>
        <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', pointerEvents: 'auto' }}>
          <Tooltip title={index === slides.length - 1 ? t("No next slide available") : t("Next slide")} arrow>
            <span>
              <button
                className={"button-circular " + (index === slides.length - 1 ? "disabled" : "")}
                onClick={() => onChange(index + 1)}
                disabled={index === slides.length - 1}
                style={{
                  width: 40,
                  height: 40,
                  fontSize: 20
                }}
                aria-label={t("Next slide")}
              >
                <Icon path={mdiArrowRight} size={1.2} />
              </button>
            </span>
          </Tooltip>
        </div>
      </div>
      {/* Slide indicators */}
      <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'center', gap: 8 }}>
        {slides.map((_, i) => (
          <Tooltip key={i} title={`${t("Go to slide")} ${i + 1}`} arrow>
            <span>
              <button
                className="button-circular"
                onClick={() => onChange(i)}
                style={{
                  width: 28,
                  height: 28,
                  background: i === index ? 'revert-layer' : '#fff',
                  color: i === index ? '#fff' : 'var(--mui-palette-primary-main)',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
                  fontSize: 16,
                  lineHeight: 1
                }}
                aria-label={`${t("Go to slide")} ${i + 1}`}
              >
                {i + 1}
              </button>
            </span>
          </Tooltip>
        ))}
      </div>
    </div>
  );
};

export default SlideShow;
